import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { apiService, type EnvironmentVariable } from '../services/apiService';
import { environmentPlaceholderSx } from '../utils/environmentUtils';
import {
  toEnvironmentInlineRowScope,
} from '../utils/variableScope';
import {
  ENVIRONMENT_VARIABLES_CHANGED_EVENT,
  dispatchEnvironmentVariablesChanged,
  type EnvironmentVariablesChangedDetail,
} from '../utils/environmentVariablesEvents';
import {
  clearManageAccountCache,
  getManageAccountCacheKey,
} from './manageAccount/manageAccountSessionCache';
import { getHostTenantSlug } from '../utils/tenantResolver';
import {
  bumpSaveVersion,
  mergeCreatedIdIntoRows,
  resolveInlineVariableSaveAction,
  shouldApplySaveResult,
} from '../utils/environmentVariableInlineSave';

/** Viewport anchorPosition for MUI Select (stable under CSS zoom; opens above/below the field). */
function useZoomSafeSelectMenu(extra?: {
  paperSx?: Record<string, unknown>;
  menuListSx?: Record<string, unknown>;
}) {
  const [menuState, setMenuState] = useState({
    open: false,
    top: 0,
    left: 0,
    width: 0,
    openUpward: false,
  });
  const anchorElRef = useRef<HTMLElement | null>(null);

  const measureFromEl = (combobox: HTMLElement) => {
    const rect = combobox.getBoundingClientRect();
    const gap = 4;
    const spaceBelow = typeof window !== 'undefined' ? window.innerHeight - rect.bottom : 320;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < 120 && spaceAbove > spaceBelow;
    return {
      top: Math.round(openUpward ? rect.top - gap : rect.bottom + gap),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      openUpward,
    };
  };

  const onOpen = (e: React.SyntheticEvent) => {
    const el = e.currentTarget as HTMLElement;
    const combobox =
      (el.matches('[role="combobox"]')
        ? el
        : el.querySelector<HTMLElement>('[role="combobox"]')) ?? el;
    anchorElRef.current = combobox;
    const measured = measureFromEl(combobox);
    flushSync(() => {
      setMenuState({
        open: true,
        ...measured,
      });
    });
  };

  const onClose = () => setMenuState((prev) => ({ ...prev, open: false }));

  const remeasureWhileOpen = () => {
    const combobox = anchorElRef.current;
    if (!combobox) return;
    const measured = measureFromEl(combobox);
    setMenuState((prev) =>
      prev.open
        ? {
            ...prev,
            ...measured,
          }
        : prev
    );
  };

  const MenuProps = {
    disableScrollLock: true,
    anchorReference: 'anchorPosition' as const,
    anchorPosition: { top: menuState.top, left: menuState.left },
    transformOrigin: {
      vertical: (menuState.openUpward ? 'bottom' : 'top') as 'bottom' | 'top',
      horizontal: 'left' as const,
    },
    sx: { zIndex: (theme: import('@mui/material/styles').Theme) => theme.zIndex.modal },
    TransitionProps: {
      onEntering: remeasureWhileOpen,
      onEntered: remeasureWhileOpen,
    },
    PaperProps: {
      sx: {
        ...(menuState.openUpward ? { mb: 0.5, mt: 0 } : { mt: 0.5 }),
        zIndex: (theme: import('@mui/material/styles').Theme) => theme.zIndex.modal,
        ...(menuState.width > 0
          ? {
              width: menuState.width,
              minWidth: menuState.width,
              maxWidth: 'min(100vw - 16px, 560px)',
            }
          : {}),
        ...extra?.paperSx,
      },
    },
    MenuListProps: {
      sx: {
        maxHeight: 320,
        overflowY: 'auto' as const,
        py: 0.5,
        ...extra?.menuListSx,
      },
    },
  };

  return { menuOpen: menuState.open, onOpen, onClose, MenuProps };
}

type ZoomSafeScopeSelectProps = {
  value: 'environment' | 'project';
  onChange: (scope: 'environment' | 'project') => void;
};

function ZoomSafeScopeSelect({ value, onChange }: ZoomSafeScopeSelectProps) {
  const zoomMenu = useZoomSafeSelectMenu({
    paperSx: {
      '& .MuiMenuItem-root': {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.4375,
        minHeight: 36,
        py: 0.5,
      },
    },
  });

  return (
    <TextField
      select
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value as 'environment' | 'project')}
      SelectProps={{
        open: zoomMenu.menuOpen,
        onOpen: zoomMenu.onOpen,
        onClose: zoomMenu.onClose,
        MenuProps: zoomMenu.MenuProps,
      }}
      sx={{
        minWidth: 0,
        width: '100%',
        /* MUI Select adds minHeight on .MuiSelect-select; without this the row is taller than other small TextFields. */
        '& .MuiOutlinedInput-root': {
          minHeight: 40,
          height: 40,
          boxSizing: 'border-box',
        },
        '& .MuiSelect-select': {
          minHeight: '0 !important',
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: 1.4375,
          whiteSpace: 'nowrap',
          overflow: 'visible',
          textOverflow: 'clip',
          pr: '32px !important',
        },
        '& .MuiSelect-icon': {
          right: 8,
        },
      }}
    >
      <MenuItem value="environment">Environment</MenuItem>
      <MenuItem value="project">Project</MenuItem>
    </TextField>
  );
}

type InlineRow = {
  rowKey: string;
  id?: number;
  tempId?: string;
  key: string;
  value: string;
  scope: 'environment' | 'project';
  originalScope?: 'environment' | 'project';
  projectId?: number;
  description: string;
  selected: boolean;
};

function newTempId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function isEnvironmentOrProjectScoped(v: EnvironmentVariable, environmentId?: number): boolean {
  const rawScope = (v.scope ?? '').toString().toLowerCase().trim().replace(/_/g, '-');
  const hasDynamicExtractionMeta =
    !!v.extractFromResponse ||
    !!(v.extractJsonPath && String(v.extractJsonPath).trim()) ||
    !!(v.extractHeaderName && String(v.extractHeaderName).trim());
  if (hasDynamicExtractionMeta) return false;
  if (rawScope === 'request' || rawScope === 'collection' || rawScope === 'sub-collection') {
    return false;
  }
  if (rawScope === 'environment') return true;
  if (rawScope === 'project') {
    // Project-scoped rows are shared across all environments in the same project.
    return true;
  }

  if (!rawScope) {
    const anyVar = v as any;
    const hasRequestOrCollectionScope =
      anyVar?.requestId != null || anyVar?.collectionId != null;
    if (hasRequestOrCollectionScope) return false;

    if (v.environmentId != null) {
      return environmentId == null || Number(v.environmentId) === Number(environmentId);
    }
    if (v.projectId != null) {
      return false;
    }
  }
  return false;
}

function fromApi(v: EnvironmentVariable): InlineRow {
  const idPart = v.id != null ? `id-${v.id}` : newTempId();
  const scope = toEnvironmentInlineRowScope(v);
  return {
    rowKey: idPart,
    id: v.id,
    key: v.key ?? '',
    value: v.value ?? '',
    scope,
    originalScope: scope,
    projectId: v.projectId,
    description: v.description ?? '',
    selected: false,
  };
}

function makeEmptyRow(): InlineRow {
  const tempId = newTempId();
  return {
    rowKey: `tmp-${tempId}`,
    tempId,
    key: '',
    value: '',
    scope: 'environment',
    description: '',
    selected: false
  };
}

/** Blank spare row at the bottom (query-param style): no id, no key/value/description. */
function isEmptySpare(row: InlineRow): boolean {
  return (
    row.id == null &&
    !row.key.trim() &&
    !row.value.trim() &&
    !(row.description ?? '').trim()
  );
}

function ensureTrailingBlank(rows: InlineRow[]): InlineRow[] {
  if (rows.length === 0) return [makeEmptyRow()];
  if (isEmptySpare(rows[rows.length - 1])) return rows;
  return [...rows, makeEmptyRow()];
}

const DUPLICATE_KEY_MESSAGE = 'Variable already exists with the same name';

function scopeKey(row: InlineRow): string {
  const keyNorm = (row.key ?? '').trim();
  if (!keyNorm) return '';
  const scope = (row.scope ?? 'environment').toLowerCase();
  return `${scope}:${keyNorm}`;
}

/** First row index for this scope+key (skips empty spare rows). */
function firstIndexForScopeKey(rows: InlineRow[], scopeKeyValue: string): number {
  for (let i = 0; i < rows.length; i++) {
    if (isEmptySpare(rows[i])) continue;
    if (scopeKey(rows[i]) === scopeKeyValue) return i;
  }
  return -1;
}

function isFocusMovingToInput(
  targetInput: HTMLInputElement | null | undefined,
  relatedTarget: EventTarget | null
): boolean {
  if (!targetInput || !relatedTarget) return false;
  return relatedTarget === targetInput || targetInput.contains(relatedTarget as Node);
}
function isDuplicateKeyRow(rows: InlineRow[], index: number): boolean {
  const sk = scopeKey(rows[index]);
  if (!sk) return false;
  const first = firstIndexForScopeKey(rows, sk);
  return first !== -1 && first !== index;
}

interface EnvironmentVariablesInlineProps {
  environmentId: number;
  projectId?: number;
}

/** Full-width layout: fr columns absorb leftover space; minmax(0) so 1366 / 14" Mac don't clip. */
const gridRowSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '40px minmax(88px, 1fr) minmax(88px, 1fr) 140px minmax(64px, 0.55fr) 36px',
    md: '40px minmax(0, 1fr) minmax(0, 1fr) 148px minmax(0, 0.55fr) 36px',
    lg: '44px minmax(0, 1fr) minmax(0, 1.15fr) 156px minmax(0, 0.65fr) 40px',
  },
  gap: { xs: 0.75, md: 1 },
  width: '100%',
  minWidth: { xs: 560, md: 0 },
  alignItems: 'center' as const,
  boxSizing: 'border-box' as const,
};

const EnvironmentVariablesInline: React.FC<EnvironmentVariablesInlineProps> = ({ environmentId, projectId }) => {
  const [rows, setRows] = useState<InlineRow[]>([]);
  const [environmentProjectId, setEnvironmentProjectId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rowsRef = useRef(rows);
  const valueInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const environmentIdRef = useRef(environmentId);
  const loadingRef = useRef(loading);
  const loadGenerationRef = useRef(0);
  const persistGenerationRef = useRef(0);
  const saveVersionsRef = useRef<Record<string, number>>({});
  const saveTimersRef = useRef<Record<string, number>>({});
  const saveChainsRef = useRef<Record<string, Promise<void>>>({});

  /** Keep ref aligned with committed state (do not assign during render — that can race with batched onChange). */
  useLayoutEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useLayoutEffect(() => {
    environmentIdRef.current = environmentId;
    persistGenerationRef.current += 1;
  }, [environmentId]);

  useEffect(() => {
    return () => {
      persistGenerationRef.current += 1;
      Object.values(saveTimersRef.current).forEach((timer) => window.clearTimeout(timer));
      saveTimersRef.current = {};
    };
  }, []);

  useLayoutEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const setVisibleError = useCallback((message: string | null) => {
    if (!message) {
      setError(null);
      return;
    }
    const normalized = message.toLowerCase();
    // Backend guidance message; not actionable for users in this screen.
    if (normalized.includes('use environment variable apis for environment scope')) {
      setError(null);
      return;
    }
    setError(message);
  }, []);

  const load = useCallback(async () => {
    const generation = ++loadGenerationRef.current;
    setLoading(true);
    setError(null);
    try {
      const envDetails = await apiService.getEnvironment(environmentId);
      if (generation !== loadGenerationRef.current) return;
      const resolvedProjectId =
        projectId ??
        (envDetails as any)?.projectId ??
        ((envDetails as any)?.project?.id != null ? Number((envDetails as any).project.id) : undefined);
      if (generation !== loadGenerationRef.current) return;
      const raw = await apiService.getEnvironmentVariables(environmentId);
      if (generation !== loadGenerationRef.current) return;
      const supported = (raw || [])
        .filter((v) => isEnvironmentOrProjectScoped(v, environmentId))
        .map(fromApi);
      setEnvironmentProjectId(resolvedProjectId);
      setRows(ensureTrailingBlank(supported));
    } catch (e: unknown) {
      if (generation !== loadGenerationRef.current) return;
      const msg = e instanceof Error ? e.message : 'Failed to load variables';
      setVisibleError(msg);
      setRows([makeEmptyRow()]);
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [environmentId, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onVariablesChanged = (event: Event) => {
      const detail = (event as CustomEvent<EnvironmentVariablesChangedDetail>).detail;
      if (detail?.environmentId == null) return;
      if (Number(detail.environmentId) !== Number(environmentId)) return;
      // Ignore our own saves — local state is already up to date.
      if (detail.source === 'environments-inline') return;
      void load();
    };
    window.addEventListener(ENVIRONMENT_VARIABLES_CHANGED_EVENT, onVariablesChanged);
    return () => window.removeEventListener(ENVIRONMENT_VARIABLES_CHANGED_EVENT, onVariablesChanged);
  }, [environmentId, load]);

  const notifyVariablesChanged = useCallback(() => {
    clearManageAccountCache('subscription', getManageAccountCacheKey(null, getHostTenantSlug()));
    dispatchEnvironmentVariablesChanged({
      environmentId,
      projectId: projectId ?? environmentProjectId,
      source: 'environments-inline',
    });
  }, [environmentId, projectId, environmentProjectId]);

  useEffect(() => {
    setPage(0);
  }, [environmentId]);

  const { dataRowIndices, spareRowIndex } = useMemo(() => {
    const indices: number[] = [];
    let spare: number | null = null;
    rows.forEach((row, i) => {
      if (i === rows.length - 1 && isEmptySpare(row)) {
        spare = i;
      } else {
        indices.push(i);
      }
    });
    return { dataRowIndices: indices, spareRowIndex: spare };
  }, [rows]);

  const dataRowCount = dataRowIndices.length;
  const paginationItemCount = dataRowCount + (spareRowIndex != null ? 1 : 0);
  const maxPage = Math.max(0, Math.ceil(paginationItemCount / rowsPerPage) - 1);
  const effectivePage = Math.min(page, maxPage);

  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, maxPage]);

  const visibleIndices = useMemo(() => {
    const start = effectivePage * rowsPerPage;
    const end = Math.min(start + rowsPerPage, paginationItemCount);
    const indices: number[] = [];
    for (let slot = start; slot < end; slot++) {
      if (slot < dataRowCount) {
        indices.push(dataRowIndices[slot]);
      } else if (spareRowIndex != null) {
        indices.push(spareRowIndex);
      }
    }
    return indices;
  }, [dataRowIndices, dataRowCount, spareRowIndex, effectivePage, rowsPerPage, paginationItemCount]);

  const paginatedIndices = useMemo(
    () => (spareRowIndex == null ? visibleIndices : visibleIndices.filter((index) => index !== spareRowIndex)),
    [visibleIndices, spareRowIndex]
  );

  const allSelected = useMemo(
    () =>
      paginatedIndices.length > 0 &&
      paginatedIndices.every((index) => rows[index]?.selected),
    [paginatedIndices, rows]
  );
  const someSelectedOnPage = useMemo(
    () => paginatedIndices.some((index) => rows[index]?.selected),
    [paginatedIndices, rows]
  );
  const someSelected = useMemo(() => rows.some((r) => r.selected), [rows]);

  const setRow = useCallback((index: number, patch: Partial<InlineRow>) => {
    setRows((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      const merged = { ...next[index], ...patch };
      if (patch.scope != null || patch.key != null) {
        const trial = [...next];
        trial[index] = merged;
        if (isDuplicateKeyRow(trial, index)) {
          merged.value = '';
        }
      }
      next[index] = merged;
      rowsRef.current = next;
      return next;
    });
  }, []);

  /** Key or value on the last spare row: append a new blank row below (query-parameter UX). */
  const updateKeyOrValue = useCallback((index: number, field: 'key' | 'value', value: string) => {
    setRows((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;

      if (field === 'value' && isDuplicateKeyRow(next, index)) {
        return prev;
      }

      const wasEmptySpare = isEmptySpare(next[index]);
      const patch: Partial<InlineRow> = { [field]: value };
      if (field === 'key') {
        const trialRows = [...next];
        trialRows[index] = { ...next[index], key: value };
        if (isDuplicateKeyRow(trialRows, index)) {
          patch.value = '';
        }
      }
      next[index] = { ...next[index], ...patch };
      const cur = next[index];
      const nowHasKv = !!cur.key.trim() || !!cur.value.trim();
      const isLast = index === next.length - 1;
      if (wasEmptySpare && nowHasKv && isLast) {
        next.push(makeEmptyRow());
      }
      rowsRef.current = next;
      return next;
    });
  }, []);

  type PersistOverrides = Partial<Pick<InlineRow, 'key' | 'value' | 'description' | 'scope'>>;

  const isPersistContextValid = useCallback((scheduledEnvironmentId?: number) => {
    return (
      (scheduledEnvironmentId == null || scheduledEnvironmentId === environmentIdRef.current) &&
      !loadingRef.current
    );
  }, []);

  const persistRowByKey = useCallback(
    async (
      rowKey: string,
      version: number,
      overrides: PersistOverrides | undefined,
      scheduledEnvironmentId?: number
    ) => {
      if (!isPersistContextValid(scheduledEnvironmentId)) {
        return;
      }
      if (!shouldApplySaveResult(saveVersionsRef.current, rowKey, version)) {
        return;
      }

      const index = rowsRef.current.findIndex((r) => r.rowKey === rowKey);
      if (index < 0) return;
      const base = rowsRef.current[index];
      if (!base) return;
      const row = { ...base, ...overrides };

      const keyTrim = row.key.trim();
      const value = row.value;
      const description = row.description ?? '';
      const scope = row.scope ?? 'environment';
      const effectiveEnvironmentId = scheduledEnvironmentId ?? environmentId;
      const effectiveProjectId = row.projectId ?? environmentProjectId;
      setError(null);

      if (isDuplicateKeyRow(rowsRef.current, index)) {
        setVisibleError(DUPLICATE_KEY_MESSAGE);
        return;
      }

      const saveAction = resolveInlineVariableSaveAction(row);
      if (saveAction === 'skip') {
        return;
      }

      const mergeCreatedId = (createdId: number | undefined, replaceExisting = false) => {
        if (createdId == null) return;
        setRows((prev) => {
          const next = mergeCreatedIdIntoRows(prev, rowKey, createdId, { replaceExisting });
          rowsRef.current = next;
          return next;
        });
      };

      const revertScopeOnFailure = () => {
        setRows((prev) => {
          const next = prev.map((r) => {
            if (r.rowKey !== rowKey) return r;
            if (r.originalScope && r.originalScope !== r.scope) {
              return { ...r, scope: r.originalScope };
            }
            return r;
          });
          rowsRef.current = next;
          return next;
        });
      };

      setSaving(true);
      try {
        if (saveAction === 'delete' && row.id != null) {
          if ((row.scope ?? 'environment') === 'project') {
            await apiService.deleteVariable(row.id);
          } else {
            await apiService.deleteEnvironmentVariable(effectiveEnvironmentId, row.id);
          }
          if (!isPersistContextValid(scheduledEnvironmentId)) return;
          if (!shouldApplySaveResult(saveVersionsRef.current, rowKey, version)) return;
          setRows((prev) => {
            const next = ensureTrailingBlank(prev.filter((r) => r.rowKey !== rowKey));
            rowsRef.current = next;
            return next;
          });
          notifyVariablesChanged();
          return;
        }

        if (saveAction === 'update' && row.id != null) {
          const originalScope = row.originalScope ?? row.scope;
          if (scope === 'project') {
            if (effectiveProjectId == null) {
              setError('Project scope requires a project context. Please re-open this environment and try again.');
              revertScopeOnFailure();
              return;
            }
            await apiService.updateVariable(row.id, {
              id: row.id,
              name: keyTrim,
              value,
              scope: 'project',
              projectId: effectiveProjectId,
              environmentId: originalScope === 'environment' ? null : undefined,
              description,
            });
          } else if (originalScope === 'project') {
            await apiService.deleteVariable(row.id);
            if (!isPersistContextValid(scheduledEnvironmentId)) return;
            const createdEnvVar = await apiService.createEnvironmentVariable(effectiveEnvironmentId, {
              key: keyTrim,
              value,
              description,
            });
            if (!isPersistContextValid(scheduledEnvironmentId)) return;
            mergeCreatedId(createdEnvVar.id, true);
            if (!shouldApplySaveResult(saveVersionsRef.current, rowKey, version)) return;
            setRows((prev) => {
              const next = prev.map((r) =>
                r.rowKey === rowKey
                  ? {
                      ...r,
                      id: createdEnvVar.id ?? r.id,
                      originalScope: 'environment' as const,
                      projectId: undefined,
                    }
                  : r
              );
              rowsRef.current = next;
              return next;
            });
            notifyVariablesChanged();
            return;
          } else {
            await apiService.updateEnvironmentVariable(effectiveEnvironmentId, row.id, {
              key: keyTrim,
              value,
              description,
            });
          }
          if (!isPersistContextValid(scheduledEnvironmentId)) return;
          if (!shouldApplySaveResult(saveVersionsRef.current, rowKey, version)) return;
          setRows((prev) => {
            const next = prev.map((r) =>
              r.rowKey === rowKey
                ? {
                    ...r,
                    originalScope: scope,
                    projectId: scope === 'project' ? effectiveProjectId : undefined,
                  }
                : r
            );
            rowsRef.current = next;
            return next;
          });
          notifyVariablesChanged();
          return;
        }

        if (saveAction === 'create') {
          const created =
            scope === 'project'
              ? await (() => {
                  if (effectiveProjectId == null) {
                    throw new Error(
                      'Project scope requires a project context. Please re-open this environment and try again.'
                    );
                  }
                  return apiService.createVariable({
                    name: keyTrim,
                    value,
                    scope: 'project',
                    projectId: effectiveProjectId,
                    description,
                  });
                })()
              : await apiService.createEnvironmentVariable(effectiveEnvironmentId, {
                  key: keyTrim,
                  value,
                  description,
                });
          if (!isPersistContextValid(scheduledEnvironmentId)) return;
          mergeCreatedId(created.id);
          if (!shouldApplySaveResult(saveVersionsRef.current, rowKey, version)) return;
          const createdScope: 'environment' | 'project' =
            (created.scope || scope).toLowerCase().trim() === 'project' ? 'project' : 'environment';
          setRows((prev) => {
            const next = prev.map((r) =>
              r.rowKey === rowKey
                ? {
                    ...r,
                    id: r.id ?? created.id,
                    originalScope: createdScope,
                    projectId: created.projectId ?? (scope === 'project' ? effectiveProjectId : undefined),
                  }
                : r
            );
            rowsRef.current = next;
            return next;
          });
          notifyVariablesChanged();
        }
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error ||
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (e instanceof Error ? e.message : 'Failed to save variable');
        setVisibleError(msg);
        revertScopeOnFailure();
      } finally {
        setSaving(false);
      }
    },
    [environmentId, environmentProjectId, isPersistContextValid, notifyVariablesChanged]
  );

  /**
   * One pending save per rowKey. Newer edits replace the debounce and supersede older versions.
   * The request is always built from the latest row (plus optional field overrides from blur).
   */
  const schedulePersistByRowKey = useCallback(
    (rowKey: string, overrides?: PersistOverrides) => {
      const envAtSchedule = environmentIdRef.current;
      const generationAtSchedule = loadGenerationRef.current;
      const persistAtSchedule = persistGenerationRef.current;
      const version = bumpSaveVersion(saveVersionsRef.current, rowKey);
      if (saveTimersRef.current[rowKey] != null) {
        window.clearTimeout(saveTimersRef.current[rowKey]);
      }
      saveTimersRef.current[rowKey] = window.setTimeout(() => {
        delete saveTimersRef.current[rowKey];
        if (environmentIdRef.current !== envAtSchedule) return;
        if (loadGenerationRef.current !== generationAtSchedule) return;
        if (persistGenerationRef.current !== persistAtSchedule) return;
        if (loadingRef.current) return;
        if (!shouldApplySaveResult(saveVersionsRef.current, rowKey, version)) return;
        const prev = saveChainsRef.current[rowKey] ?? Promise.resolve();
        saveChainsRef.current[rowKey] = prev
          .catch(() => undefined)
          .then(() => persistRowByKey(rowKey, version, overrides, envAtSchedule));
      }, 0);
    },
    [persistRowByKey]
  );

  const schedulePersistRow = useCallback(
    (index: number, overrides?: PersistOverrides) => {
      const row = rowsRef.current[index];
      if (!row) return;
      schedulePersistByRowKey(row.rowKey, overrides);
    },
    [schedulePersistByRowKey]
  );

  const handleDeleteAt = useCallback(
    async (index: number) => {
      const row = rowsRef.current[index];
      if (!row) return;
      setError(null);
      if (row.id != null) {
        setSaving(true);
        try {
          const scope = (row.scope ?? 'environment') as 'environment' | 'project';
          try {
            if (scope === 'project') {
              await apiService.deleteVariable(row.id);
            } else {
              await apiService.deleteEnvironmentVariable(environmentId, row.id);
            }
          } catch (primaryErr) {
            // Some deployments return merged/mirrored variables where `id` belongs to the other API.
            // Fall back to the alternate delete endpoint so DB deletion still occurs.
            if (scope === 'project') {
              await apiService.deleteEnvironmentVariable(environmentId, row.id);
            } else {
              await apiService.deleteVariable(row.id);
            }
          }
          setRows((prev) => ensureTrailingBlank(prev.filter((_, i) => i !== index)));
          notifyVariablesChanged();
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : 'Failed to delete variable';
          setVisibleError(msg);
        } finally {
          setSaving(false);
        }
      } else {
        setRows((prev) => ensureTrailingBlank(prev.filter((_, i) => i !== index)));
      }
    },
    [environmentId, notifyVariablesChanged]
  );

  const handleDeleteSelected = useCallback(async () => {
    const selected = rowsRef.current.filter((r) => r.selected);
    if (selected.length === 0) return;
    setError(null);
    setSaving(true);
    try {
      let deletedPersisted = false;
      for (const row of selected) {
        if (row.id != null) {
          const scope = (row.scope ?? 'environment') as 'environment' | 'project';
          try {
            if (scope === 'project') {
              await apiService.deleteVariable(row.id);
            } else {
              await apiService.deleteEnvironmentVariable(environmentId, row.id);
            }
          } catch (_primaryErr) {
            if (scope === 'project') {
              await apiService.deleteEnvironmentVariable(environmentId, row.id);
            } else {
              await apiService.deleteVariable(row.id);
            }
          }
          deletedPersisted = true;
        }
      }
      setRows((prev) => ensureTrailingBlank(prev.filter((r) => !r.selected)));
      if (deletedPersisted) {
        notifyVariablesChanged();
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to delete variables';
      setVisibleError(msg);
    } finally {
      setSaving(false);
    }
  }, [environmentId, notifyVariablesChanged]);

  const toggleSelectAll = useCallback(() => {
    const next = !allSelected;
    setRows((prev) => {
      const copy = [...prev];
      paginatedIndices.forEach((index) => {
        if (copy[index]) {
          copy[index] = { ...copy[index], selected: next };
        }
      });
      return copy;
    });
  }, [allSelected, paginatedIndices]);

  const toggleRowSelected = useCallback((index: number) => {
    setRows((prev) => {
      const copy = [...prev];
      if (!copy[index]) return prev;
      copy[index] = { ...copy[index], selected: !copy[index].selected };
      return copy;
    });
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, ...environmentPlaceholderSx }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' }, minWidth: 0 }}>
        <Typography variant="h6" sx={{ m: 0, fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.02em', lineHeight: '36px', height: 36 }}>
          Variables
        </Typography>
        {someSelected && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteSelected}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              height: 36,
              minHeight: 36,
              whiteSpace: 'nowrap',
            }}
          >
            Delete selected
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          display: 'block',
          overflowX: 'auto',
        }}
      >
        <Box
          sx={{
            px: { xs: 1, sm: 1.25 },
            pt: 1.25,
            pb: 1.25,
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
        {/* Header */}
        <Box
          sx={{
            ...gridRowSx,
            pb: 1,
            borderBottom: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Checkbox
              indeterminate={someSelectedOnPage && !allSelected}
              checked={allSelected}
              onChange={toggleSelectAll}
              size="small"
              disabled={paginatedIndices.length === 0 || saving}
              inputProps={{ 'aria-label': 'Select all variables' }}
            />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Key
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Value
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Scope
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Description
          </Typography>
          <Box />
        </Box>

        {/* Rows */}
        <Box sx={{ width: '100%', minWidth: 0 }}>
          {visibleIndices.map((index) => {
            const row = rows[index];
            if (!row) return null;
            return (
            <Box
              key={row.rowKey}
              sx={{
                ...gridRowSx,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Checkbox
                  checked={row.selected}
                  onChange={() => toggleRowSelected(index)}
                  size="small"
                  disabled={saving}
                />
              </Box>
              <TextField
                fullWidth
                size="small"
                value={row.key}
                onChange={(e) => updateKeyOrValue(index, 'key', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault();
                    valueInputRefs.current[row.rowKey]?.focus();
                  }
                }}
                onBlur={(e) => {
                  if (isFocusMovingToInput(valueInputRefs.current[row.rowKey], e.relatedTarget)) {
                    return;
                  }
                  const keyVal = (e.target as HTMLInputElement).value;
                  if (keyVal.trim() && isDuplicateKeyRow(rowsRef.current, index)) {
                    setVisibleError(DUPLICATE_KEY_MESSAGE);
                  }
                  schedulePersistRow(index, { key: keyVal });
                }}
                placeholder="KEY_NAME"
                sx={{ minWidth: 0, width: '100%' }}
              />
              <TextField
                fullWidth
                size="small"
                value={row.value}
                onChange={(e) => updateKeyOrValue(index, 'value', e.target.value)}
                inputRef={(el) => {
                  valueInputRefs.current[row.rowKey] = el;
                }}
                onBlur={(e) => {
                  if (isDuplicateKeyRow(rowsRef.current, index)) {
                    setVisibleError(DUPLICATE_KEY_MESSAGE);
                    return;
                  }
                  schedulePersistRow(index, { value: (e.target as HTMLInputElement).value });
                }}
                placeholder="Value"
                disabled={saving || isDuplicateKeyRow(rows, index)}
                sx={{ minWidth: 0, width: '100%' }}
              />
              <ZoomSafeScopeSelect
                value={row.scope}
                onChange={(scope) => {
                  setRow(index, { scope });
                  if (isDuplicateKeyRow(rowsRef.current, index)) {
                    setVisibleError(DUPLICATE_KEY_MESSAGE);
                    return;
                  }
                  schedulePersistRow(index, { scope });
                }}
              />
              <TextField
                fullWidth
                size="small"
                value={row.description}
                onChange={(e) => setRow(index, { description: e.target.value })}
                onBlur={(e) =>
                  schedulePersistRow(index, { description: (e.target as HTMLInputElement).value })
                }
                placeholder="Description"
                sx={{ minWidth: 0, width: '100%' }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  aria-label="Delete row"
                  onClick={() => handleDeleteAt(index)}
                  disabled={saving}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            );
          })}
        </Box>
        </Box>
      </Box>
      {dataRowCount > 0 && (
        <TablePagination
          component="div"
          count={paginationItemCount}
          page={effectivePage}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            mt: 0.5,
            overflow: 'hidden',
            '& .MuiTablePagination-toolbar': {
              flexWrap: 'wrap',
              minHeight: 36,
              px: 0.5,
              gap: 0.5,
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.8125rem',
              m: 0,
            },
            '& .MuiTablePagination-select': {
              fontSize: '0.8125rem',
            },
            '& .MuiTablePagination-actions .MuiIconButton-root': {
              p: 0.5,
            },
          }}
        />
      )}
    </Box>
  );
};

export default EnvironmentVariablesInline;
